import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Col, Divider, Flex, Row, Steps, Typography } from "antd";
import "./system-check-steps.less";
import useCounter from "../../../hooks/use-counter";
import useSystemCheckSteps, {
  SystemCheckStepper,
  SystemCheckStepperEnum,
} from "../../../hooks/use-system-check-steps";
import PprButton from "../../shared/ppr-button/ppr-button";
import { useTranslation } from "react-i18next";
import Screen, { ScreenResolutionOptions } from "./screen/screen";
import NetworkCheck from "./network-check/network-check";
import VideoCheck from "./video-check";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faOctagonExclamation } from "@awesome.me/kit-b311a11e92/icons/classic/solid";
import Microphone from "./micro-check";
import SystemCompatibility, {
  OperatingSystemCheckOption,
} from "./system-compatibility/system-compatibility";
import Summary from "./summary/summary";

const { Title, Text } = Typography;

export type SystemCheckConfiguration<TOptions> = {
  enable: boolean;
  options?: TOptions;
};

export interface SystemCheckConfigurations {
  browserCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  deviceCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  operatingSystemCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  screenResolutionCheck?: SystemCheckConfiguration<ScreenResolutionOptions>;
  screenSharingCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  multipleMonitorCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  webcamCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  microphoneCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  urlCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  networkDownloadCheck?: SystemCheckConfiguration<Record<string, unknown>>;
  networkUploadCheck?: SystemCheckConfiguration<Record<string, unknown>>;
}

export interface SystemCheckProps {
  configurations: SystemCheckConfigurations;
  onSystemCheckComplete?: () => void;
}

function SystemCheckSteps(props: SystemCheckProps) {
  const {
    browserCheck,
    deviceCheck,
    operatingSystemCheck,
    screenResolutionCheck,
    multipleMonitorCheck,
    webcamCheck,
    microphoneCheck,
    urlCheck,
    networkDownloadCheck,
    networkUploadCheck,
  } = props.configurations;

  const { t } = useTranslation();

  const systemCheckSteps = useSystemCheckSteps(props.configurations);
  const [isNext, setIsNext] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [showRetryBtn, setShowRetryBtn] = useState(false);
  const [retry, setRetry] = useState(0);
  const [errors, setErrors] = useState<ReactNode[]>([]);
  const [errorsTitle, setErrorTitle] = useState<string>("");
  const [showFAQLink, setShowFQALink] = useState<boolean>(true);

  const lastStepIdx = useMemo(() => {
    const countStep = systemCheckSteps.filter(
      (s) => s.name !== SystemCheckStepperEnum.Summary,
    ).length;
    return countStep > 0 ? countStep - 1 : 0;
  }, [systemCheckSteps]);

  const [currentStep, handlers] = useCounter(0, {
    min: 0,
    max: systemCheckSteps.length - 1,
  });

  useEffect(() => {
    const allChecksDisabled =
      !(browserCheck?.enable ?? false) &&
      !(deviceCheck?.enable ?? false) &&
      !(operatingSystemCheck?.enable ?? false) &&
      !(screenResolutionCheck?.enable ?? false) &&
      !(multipleMonitorCheck?.enable ?? false) &&
      !(webcamCheck?.enable ?? false) &&
      !(microphoneCheck?.enable ?? false) &&
      !(urlCheck?.enable ?? false) &&
      !(networkDownloadCheck?.enable ?? false) &&
      !(networkUploadCheck?.enable ?? false);
    if (allChecksDisabled) {
      setIsDone(true);
    }
  }, [
    browserCheck,
    deviceCheck,
    operatingSystemCheck,
    screenResolutionCheck,
    multipleMonitorCheck,
    webcamCheck,
    microphoneCheck,
    urlCheck,
    networkDownloadCheck,
    networkUploadCheck,
  ]);

  const handleNext = () => {
    setIsNext(false);
    setShowRetryBtn(false);
    if (currentStep === lastStepIdx) {
      setIsDone(true);
    }
    handlers.increment();
  };

  const handleCompleteStep = () => {
    setIsNext(true);
  };

  const handleFailStep = (
    errorList: ReactNode[],
    title?: string,
    showFAQLink = false,
    canRetry?: boolean,
  ) => {
    if (canRetry) {
      setShowRetryBtn(true);
    }
    setIsNext(false);
    setTimeout(() => {
      const checkTitle = title ?? "";

      setErrors(errorList);
      setErrorTitle(checkTitle);
      setShowFQALink(showFAQLink);
    }, 1000);
  };

  const handleRetry = () => {
    setShowRetryBtn(false);
    setErrors([]);
    setErrorTitle("");
    setShowFQALink(false);
    setRetry((prev) => prev + 1);
  };

  const renderContent = (
    systemCheckSteps: SystemCheckStepper[],
    currentStep: number,
  ) => {
    switch (systemCheckSteps[currentStep]?.name) {
      case SystemCheckStepperEnum.SystemCompatibility:
        return (
          <SystemCompatibility
            browserCheck={!!browserCheck?.enable}
            deviceCheck={!!deviceCheck?.enable}
            operatingSystemCheck={!!operatingSystemCheck?.enable}
            systemCompatibilityCheckOption={{
              operatingSystemCheck:
                operatingSystemCheck?.options as OperatingSystemCheckOption,
            }}
            onComplete={handleCompleteStep}
            onFail={handleFailStep}
          />
        );
      case SystemCheckStepperEnum.Screen:
        return (
          <Screen
            minScreenSolution={
              props.configurations.screenResolutionCheck?.options
                ?.minScreenSolution
            }
            screenResolutionCheck={!!screenResolutionCheck?.enable}
            multipleMonitorCheck={!!multipleMonitorCheck?.enable}
            retry={retry}
            onComplete={handleCompleteStep}
            onFail={handleFailStep}
          />
        );
      case SystemCheckStepperEnum.Webcam:
        return (
          <VideoCheck
            webcamCheck={!!webcamCheck?.enable}
            onComplete={handleCompleteStep}
            onFail={handleFailStep}
          />
        );
      case SystemCheckStepperEnum.Microphone:
        return (
          <Microphone
            microphoneCheck={!!microphoneCheck?.enable}
            onComplete={handleCompleteStep}
            onFail={handleFailStep}
          />
        );
      case SystemCheckStepperEnum.Network:
        return (
          <NetworkCheck
            urlCheck={!!urlCheck?.enable}
            networkDownloadCheck={!!networkDownloadCheck?.enable}
            networkUploadCheck={!!networkUploadCheck?.enable}
            retry={retry}
            onComplete={handleCompleteStep}
            onFail={handleFailStep}
          ></NetworkCheck>
        );
      case SystemCheckStepperEnum.Summary:
        return <Summary></Summary>;
      default:
        return <></>;
    }
  };

  const renderErrorAlert = useCallback(
    (
      errors: ReactNode[],
      systemCheckSteps: SystemCheckStepper[],
      currentStep: number,
    ) => {
      if (errors.length > 0) {
        const FAQLink = systemCheckSteps[currentStep]?.FAQLink;

        return (
          <>
            <Alert
              type="error"
              showIcon
              aria-live="polite"
              className="error-alert"
              icon={<FontAwesomeIcon icon={faOctagonExclamation} />}
              description={
                <Flex justify="space-between" align="center" gap={16}>
                  <Flex vertical>
                    {errorsTitle !== "" && (
                      <Title level={4}>{t(errorsTitle)}</Title>
                    )}
                    <Flex vertical gap={4}>
                      {errors.length >= 2 ? (
                        <>
                          {errors.map((err, index) =>
                            React.isValidElement(err) ? (
                              <div key={index}>{err}</div>
                            ) : (
                              <Text key={index}>• {t(String(err))}</Text>
                            ),
                          )}
                          {showFAQLink && (
                            <Text>{t("SYSTEM_CHECK_STEPPER.HELP")}</Text>
                          )}
                        </>
                      ) : (
                        <>
                          {React.isValidElement(errors[0]) ? (
                            errors[0]
                          ) : (
                            <Text>
                              {t(String(errors[0]))}
                              {showFAQLink && t("SYSTEM_CHECK_STEPPER.HELP")}
                            </Text>
                          )}
                        </>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              }
              action={
                showFAQLink ? (
                  <PprButton
                    text={t("SYSTEM_CHECK_STEPPER.FINDOUTMORE")}
                    type="secondary"
                    className="find-more-btn"
                    hoverable={false}
                    onClick={() =>
                      window.open(FAQLink, "_blank", "noopener,noreferrer")
                    }
                  />
                ) : undefined
              }
            />
          </>
        );
      }
    },
    [showFAQLink],
  );

  return (
    <>
      <Row justify={"center"}>
        <Col sm={20} xxl={15} className="system-check-steps">
          <div className="title">{t("SYSTEM_CHECK_TITLE")}</div>

          <Steps
            current={currentStep}
            items={systemCheckSteps.map((s) => {
              return {
                title: t(s.i18nId),
              };
            })}
          />

          <div className="step-content">
            {renderContent(systemCheckSteps, currentStep)}
            {renderErrorAlert(errors, systemCheckSteps, currentStep)}
          </div>

          <Divider />

          <div className="button-wrapper">
            {isDone ? (
              <PprButton
                text={t("PPR_BUTTON.FINISH")}
                type="primary"
                icon="check-double"
                iconType="fa-solid"
                onClick={props.onSystemCheckComplete}
              />
            ) : (
              <>
                <PprButton
                  text={t("PPR_BUTTON.CANCEL")}
                  type="secondary"
                  onClick={props.onSystemCheckComplete}
                />
                {showRetryBtn && (
                  <PprButton
                    text={t("PPR_BUTTON.RETRY")}
                    type="primary"
                    onClick={handleRetry}
                  />
                )}
                <PprButton
                  text={t("PPR_BUTTON.PROCEED")}
                  disable={!isNext}
                  type="primary"
                  icon="check"
                  iconType="fa-solid"
                  onClick={handleNext}
                />
              </>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
}

export default SystemCheckSteps;
